import type { FC } from 'react';
import { Link } from '@compat/router';
import { Picture } from '@ui';
import icons from '@icons';
import dayjs from 'dayjs';
import UtilsTime from '@utils/time';
import { declension } from '@utils/declension';
import { recipe1 } from '@images';
import type { GetRecipesRecommendQuery } from '@schema/cart';
import type { Flatten } from '@root/src/types';

const {
  star: { yellow: starYellowFilled },
  person: { midGrey: personMidGreyFilled },
  messageCircle: { midGrey: messageCircleMidGreyFilled },
  timer: { midGrey: timerMidGreyFilled },
  calendar: { darkGrey: calendarDGFilled },
  eye: { darkGrey: eyeDGFilled },
  barChart2: { midGrey: barChart2MidGreyFilled },
  fire: { midGrey: fireMidGreyFilled }
} = icons.Fill;

const {
  chevronRight: { lightGrey: crLGOutlined }
} = icons.Outline;

type Recipe = Flatten<
  NonNullable<GetRecipesRecommendQuery['getRecipesRecommend']>
>;

const getRecipeDifficultyTitle = (value: number): string => {
  const obj = {
    0: 'Очень простой',
    1: 'Простой',
    2: 'Средний',
    3: 'Сложный',
    4: 'Очень сложный'
  };
  return obj[value as keyof typeof obj];
};

interface HorizontalRecipeProps {
  recipe: Recipe;
}

const HorizontalRecipe: FC<HorizontalRecipeProps> = ({ recipe }) => {
  const { authorItem } = recipe;
  return (
    <div className='h-[332px] sm:pb-4 flex w-full overflow-hidden bg-white rounded-lg shadow-[0px_0px_4px_rgba(0,0,0,0.15)] sm:flex-col sm:h-auto'>
      <Link
        to={`/recipes/${recipe.identifyIdURI}`}
        className='shrink-0 w-[272px] allMd:w-[252px] sm:w-full'
      >
        <img
          className='object-cover w-full h-full sm:h-60'
          src={recipe?.attachmentsItem?.cacheItem?.[0]?.filePath || recipe1}
          alt={recipe.title || ''}
        />
      </Link>

      <div className='flex-1 min-w-0 p-4 allMd:p-4 sm:p-2'>
        <div className='flex flex-wrap items-center mb-4 gap-x-5 gap-y-2 sm:hidden'>
          <div className='flex items-center text-sm'>
            <Picture
              pictureClassName={'mr-1'}
              icon={starYellowFilled}
              height='20'
              width='20'
              alt='star rating'
            />
            {recipe.rating ?? 0}
          </div>
          {recipe.date && (
            <div className='flex items-center text-sm'>
              <Picture
                icon={calendarDGFilled}
                width='20'
                height='20'
                pictureClassName={'mr-1'}
                alt='calendar icon'
              />
              {dayjs(recipe.date).format('DD MMMM YYYY')}
            </div>
          )}
          <div className='flex items-center text-sm gap-x-1'>
            <Picture icon={messageCircleMidGreyFilled} width='20' height='20' />
            {recipe.comments_number ?? 0}
          </div>
          <div className='flex items-center text-sm gap-x-1'>
            <Picture
              icon={eyeDGFilled}
              width='20'
              height='20'
              alt='view count icon'
            />
            {recipe.viewCount ?? 0}
          </div>
        </div>

        <Link to={`/recipes/${recipe.identifyIdURI}`}>
          <h3 className='mb-3 text-3xl font-medium sm:text-[22px]'>
            {recipe.title}
          </h3>
        </Link>

        {recipe.text && (
          <div
            className='mb-4 text-sm sm:hidden'
            dangerouslySetInnerHTML={{ __html: recipe.text }}
          />
        )}

        <div className='flex flex-wrap items-center mb-4 text-sm gap-x-6 gap-y-2 allMd:gap-x-2.5 sm:mb-0'>
          <div className='flex items-center gap-x-1'>
            <Picture icon={barChart2MidGreyFilled} width='20' height='20' />
            <div className='ml-1'>
              {getRecipeDifficultyTitle(recipe.recipes_difficult ?? 0)}
            </div>
          </div>
          <div className='flex items-center gap-x-1'>
            <Picture icon={personMidGreyFilled} width='20' height='20' />
            <div>
              {recipe.serving ?? 0}{' '}
              <span className='allMd:hidden'>
                {declension(recipe.serving ?? 0, [
                  'порция',
                  'порции',
                  'порций'
                ])}
              </span>
            </div>
          </div>
          <div className='flex items-center gap-x-1'>
            <Picture icon={timerMidGreyFilled} width='20' height='20' />
            {UtilsTime.minutesToStr(recipe.cooking_time ?? 0)}
          </div>
          {!!recipe.calories && (
            <div className='flex items-center gap-x-1'>
              <Picture icon={fireMidGreyFilled} width='20' height='20' />
              <p>
                {recipe.calories} кКал/
                <span className='text-[var(--dark-grey)]'>100г</span>
              </p>
            </div>
          )}
        </div>

        {!!(
          recipe.protein ||
          recipe.fat ||
          recipe.carbohydrates ||
          recipe.calories
        ) && (
          <div className='mb-4 sm:hidden'>
            <p className='text-[var(--dark-grey)] text-xs mb-2'>
              Пищевая ценность на 100 г
            </p>
            <ul className='flex flex-wrap gap-2'>
              {[
                {
                  id: 0,
                  title: 'Белки',
                  value: recipe.protein
                },
                {
                  id: 1,
                  title: 'Жиры',
                  value: recipe.fat
                },
                {
                  id: 2,
                  title: 'Углеводы',
                  value: recipe.carbohydrates
                },
                {
                  id: 3,
                  title: 'кКал',
                  value: recipe.calories
                }
              ].map(item => (
                <li
                  key={item.id}
                  className='px-2 py-1.5 rounded-xl bg-[var(--ultra-light-grey)] min-w-[102px]'
                >
                  <p className='mb-1 font-medium'>{item.value}</p>
                  <p className='text-xs text-[var(--ultra-dark-grey)]'>
                    {item.title}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}

        {authorItem?.fio && authorItem?.identify && (
          <Link
            to={`/author/${authorItem.identify}`}
            className='flex sm:hidden'
          >
            <div className='relative flex items-center group'>
              <div className='z-0 absolute -top-1 -left-1 transition-opacity opacity-0 w-[calc(100%+8px)] h-[calc(100%+8px)] rounded-xl bg-[var(--ultra-light-grey)] group-hover:opacity-100' />
              {authorItem.userItem?.attachmentsItem?.tempFullFilePath && (
                <div className='relative z-10 flex-shrink-0 mr-3 overflow-hidden rounded-full w-11 h-11'>
                  <img
                    src={authorItem.userItem.attachmentsItem.tempFullFilePath}
                    className='object-cover w-full h-full'
                    alt={authorItem.fio}
                  />
                </div>
              )}
              <div className='relative z-10'>
                <div className='mb-1 mr-3 text-sm font-medium'>
                  {authorItem.fio}
                </div>
                <div className='flex items-center text-[var(--ultra-dark-grey)] text-xs'>
                  <div className='mr-3 leading-none'>{authorItem.title}</div>
                  <div className='flex-shrink-0 flex items-center leading-none w-max text-xs allMd:w-auto text-[var(--ultra-dark-grey)]'>
                    Все рецепты автора
                    <Picture icon={crLGOutlined} width='20' height='20' />
                  </div>
                </div>
              </div>
            </div>
          </Link>
        )}
      </div>
    </div>
  );
};

export default HorizontalRecipe;
